import type {
  EventHandler,
  FC,
  PropsWithChildren,
  ReactNode,
  SyntheticEvent,
} from 'react';
import clsx from 'clsx';
import React, { useCallback, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { match, P } from 'ts-pattern';

import { Button } from '../Button/BaseButton/Button';
import { Tooltip } from '../Tooltip/Tooltip';

export type MenuItem = {
  key: string;
  display: ReactNode;
  onClick?: EventHandler<SyntheticEvent<HTMLElement>>;
  disabled?: boolean;
  dataTestId?: string;
  /** Hover text explaining the item, e.g. why it is disabled. */
  tooltipText?: string;
};

export type MenuProps = {
  items: MenuItem[];
  disabled?: boolean;
  justify?: 'justify-start' | 'justify-end';
  inverted?: boolean;
  dataTestId?: string;
  dropdownOffset?: string;
  border?: boolean;
};

type ItemProps = {
  item: MenuItem;
  closeMenu: () => void;
  inverted?: boolean;
  disabled?: boolean;
  dataTestId?: string;
  border?: boolean;
};

const Item: FC<ItemProps> = ({
  item: { key: _key, display, onClick, disabled, dataTestId, tooltipText },
  closeMenu,
  inverted,
  border,
}) => {
  const handleClick = useCallback(
    (evt: SyntheticEvent<HTMLElement>) => {
      if (onClick) {
        onClick(evt);
        closeMenu();
      }
    },
    [onClick, closeMenu]
  );

  const styles = match({
    inverted,
    disabled,
  })
    .with({ inverted: true, disabled: true }, () => 'text-negative bg-main-500')
    .with(
      { inverted: true, disabled: P.union(false, undefined) },
      () => 'bg-main text-negative hover:bg-main-500'
    )
    .with(
      { inverted: P.union(false, undefined), disabled: true },
      () => 'bg-main-200'
    )
    .with(
      {
        inverted: P.union(false, undefined),
        disabled: P.union(false, undefined),
      },
      () => 'bg-main-200 hover:bg-negative'
    )
    .exhaustive();

  const content = (
    <div
      className={clsx([
        'border-main-300',
        'box-border',
        border ? 'border-[0.5px]' : 'border-b-[0.5px]',
        'h-f2 w-full',
        'whitespace-nowrap',
        'overflow-y-hidden',
        styles,
      ])}
    >
      <Button
        data-testid={dataTestId}
        color={inverted ? 'text-negative' : 'text-main disabled:text-main-500'}
        cursor="disabled:hover:cursor-not-allowed"
        onClick={handleClick}
        disabled={disabled}
        textAlign="text-start"
        w="w-full"
        h="h-full"
      >
        {display}
      </Button>
    </div>
  );

  if (!tooltipText) {
    return content;
  }

  // The tooltip trigger lives on this wrapper, not the Button: a disabled
  // Button drops pointer events, so hover must be caught above it.
  return (
    <Tooltip
      baseComponent={content}
      tooltipComponent={
        <div className="w-[30ch] whitespace-normal break-words p-2 text-main">
          {tooltipText}
        </div>
      }
      tooltipDisabled={!tooltipText}
      placement="left-start"
      delayShow={0}
    />
  );
};

export const Menu: FC<PropsWithChildren<MenuProps>> = ({
  items,
  disabled,
  children,
  inverted,
  justify = 'justify-end',
  dataTestId,
  dropdownOffset,
  border = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const openMenu = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const onMenuClick = useCallback(() => {
    (isOpen ? closeMenu : openMenu)();
  }, [isOpen, openMenu, closeMenu]);
  useHotkeys('esc', () => closeMenu(), [closeMenu], {
    enabled: isOpen,
  });

  const onBlurHandler = useCallback(
    (evt: React.FocusEvent<HTMLElement, Element>) => {
      if (!evt.currentTarget.contains(evt.relatedTarget)) {
        closeMenu();
      }
    },
    [closeMenu]
  );

  const background = match({ isOpen, inverted })
    .with({ isOpen: true }, () => 'bg-negative' as const)
    .with(
      { inverted: true },
      () => 'bg-main enabled:hover:bg-negative' as const
    )
    .otherwise(() => 'bg-main-200 enabled:hover:bg-negative' as const);
  const color = match({ isOpen, inverted })
    .with({ isOpen: true, inverted: true }, () => 'text-main' as const)
    .with(
      { inverted: true },
      () => 'text-negative enabled:hover:text-main' as const
    )
    .otherwise(() => 'text-main disabled:text-main-500' as const);

  return (
    <div
      className={clsx('flex  items-center h-f2 relative', justify)}
      onBlur={onBlurHandler}
    >
      <Button
        type="button"
        h="h-full"
        background={background}
        color={color}
        cursor="disabled:hover:cursor-not-allowed"
        onClick={onMenuClick}
        data-testid={dataTestId ?? 'menu-button'}
        disabled={disabled}
      >
        {children}
      </Button>
      {isOpen && (
        <div
          className={clsx(
            'absolute top-f2 w-max max-w-[30ch] flex flex-col z-40 bg-canvas',
            'shadow-nice',
            dropdownOffset,
            border && 'py-[0.5px]'
          )}
          data-testid="menu-items"
        >
          {items.map((item) => (
            <Item
              key={item.key}
              item={item}
              closeMenu={closeMenu}
              inverted={inverted}
              disabled={item.disabled}
              border={border}
            />
          ))}
        </div>
      )}
    </div>
  );
};
